import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";
import { fetchLastDaysDataApiConsult } from "../../services/enhancedDashboard";
import {
  getDailyQueryPropsApi,
  updateDailyQueryPropsApi,
} from "../../services/dashboard";
import {
  DASHBOARD_DAY_RANGE_OPTIONS,
  buildDashboardFilterDateRange,
  formatDashboardFilterDateRangeLabel,
} from "../../utils/dashboardDateRange";
import { toastMessage } from "../../utils/toastMessage";
import {
  GROUP_COLORS,
  SUB_GROUP_COLORS,
  SUB_GROUP_OPTIONS,
} from "../../utils/constants";
import scrollToUp from "../../utils/scrollToUp";
import { DatePicker } from "../../components/Form";
import {
  getConsultColumnDefinitions,
  buildConsultColumnVisibilityDefaults,
  buildQuerySubGroupColumnVisibility,
  normalizeDailyConsultPerformanceData,
  getConsultCellValue,
  getConsultTotalsValue,
} from "./dashboardDailyConsultColumns";
import {
  DAILY_GROUP_ORDER,
  DAILY_GROUP_LABELS,
} from "./dashboardDailyGroupColumns";

const GROUP_FILTER_OPTIONS = [
  { key: "all", label: "All" },
  ...DAILY_GROUP_ORDER.map((key) => ({
    key,
    label:
      key === "dead"
        ? "Dead"
        : key === "n"
          ? "N"
          : key.toUpperCase(),
  })),
];

const metricGroupBorder = (column) =>
  column.metricGroupStart
    ? { borderLeft: "1px solid rgba(15, 23, 42, 0.07)" }
    : undefined;

function queryRowColors(row) {
  const groupKey = String(
    row?.group ?? row?.groupName ?? row?.groupCode ?? row?.groupId ?? ""
  )
    .toLowerCase()
    .trim();
  const normalized = groupKey === "n" ? "new" : groupKey;
  const colors = GROUP_COLORS[normalized];
  if (!colors) return null;
  return { backgroundColor: colors.bg, color: colors.text || "#fff" };
}

function rowMatchesGroupFilter(row, groupKey) {
  if (!groupKey || groupKey === "all") return true;
  const key = String(
    row?.group ?? row?.groupName ?? row?.groupCode ?? row?.groupId ?? ""
  )
    .toLowerCase()
    .trim();
  if (groupKey === "n") return key === "n" || key === "new";
  return key === groupKey;
}

const DailyConsultPerformance = () => {
  const [dailyConsultPerformance, setDailyConsultPerformance] = useState([]);
  const [dailyConsultTotals, setDailyConsultTotals] = useState(null);
  const [dailyConsultFilter, setDailyConsultFilter] = useState("today");
  const [dailyConsultCustomStart, setDailyConsultCustomStart] = useState(null);
  const [dailyConsultCustomEnd, setDailyConsultCustomEnd] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [selectedSubGroup, setSelectedSubGroup] = useState("all");
  const [loading, setLoading] = useState(false);
  const [pageConsult, setPageConsult] = useState(0);
  const [rowsPerPageConsult, setRowsPerPageConsult] = useState(10);
  const [columnVisibilityConsult, setColumnVisibilityConsult] = useState(() =>
    buildConsultColumnVisibilityDefaults()
  );
  const [isColumnSelectorOpenConsult, setIsColumnSelectorOpenConsult] =
    useState(false);
  const [queryGroupOrder, setQueryGroupOrder] = useState(["AQ", "BQ", "CQ"]);
  const [maximumPerDayQuery, setMaximumPerDayQuery] = useState(3);
  const [queryPropsSaving, setQueryPropsSaving] = useState(false);
  const columnSelectorRefConsult = useRef(null);

  const consultColumnDefinitions = getConsultColumnDefinitions(false);

  const visibleConsultColumns = consultColumnDefinitions.filter(
    (col) => columnVisibilityConsult[col.key] !== false
  );

  const filteredDailyConsultPerformance = useMemo(() => {
    const rows = dailyConsultPerformance.filter((row) => {
      if (!rowMatchesGroupFilter(row, selectedGroup)) return false;
      if (selectedSubGroup === "all") return true;
      return String(row.subGroup || "").toUpperCase() === selectedSubGroup;
    });

    if (!SUB_GROUP_OPTIONS.includes(selectedSubGroup)) return rows;

    return [...rows].sort((a, b) => {
      const completedDiff =
        (b.queryCompletedConsultation || 0) - (a.queryCompletedConsultation || 0);
      if (completedDiff !== 0) return completedDiff;
      const convertedDiff =
        (b.queryConvertedUsers || 0) - (a.queryConvertedUsers || 0);
      if (convertedDiff !== 0) return convertedDiff;
      const nameA = String(a.consultantName ?? a.name ?? "").toLowerCase();
      const nameB = String(b.consultantName ?? b.name ?? "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [dailyConsultPerformance, selectedGroup, selectedSubGroup]);

  const paginatedDailyConsultPerformance =
    filteredDailyConsultPerformance.slice(
      pageConsult * rowsPerPageConsult,
      pageConsult * rowsPerPageConsult + rowsPerPageConsult
    );

  const dailyConsultDateRangeLabel = formatDashboardFilterDateRangeLabel(
    dailyConsultFilter,
    {
      customStartDate: dailyConsultCustomStart,
      customEndDate: dailyConsultCustomEnd,
    }
  );

  const fetchDailyConsultPerformance = async (
    overrideFilter = null,
    overrideCustom = null
  ) => {
    try {
      const filterToUse = overrideFilter ?? dailyConsultFilter;
      const customStart =
        overrideCustom?.start !== undefined
          ? overrideCustom.start
          : dailyConsultCustomStart;
      const customEnd =
        overrideCustom?.end !== undefined
          ? overrideCustom.end
          : dailyConsultCustomEnd;

      if (filterToUse === "custom" && (!customStart || !customEnd)) {
        return;
      }

      const range = buildDashboardFilterDateRange(filterToUse, {
        customStartDate: customStart,
        customEndDate: customEnd,
      });
      if (!range) return;

      setLoading(true);
      const sendData = {
        ...range,
        fast: true,
      };

      const { data } = await fetchLastDaysDataApiConsult(sendData);
      if (data.code === 200) {
        const result = data?.result || {};
        const consultData = normalizeDailyConsultPerformanceData(result);
        setDailyConsultPerformance(consultData);
        setDailyConsultTotals(result?.totals || null);
        setPageConsult(0);
      } else {
        toastMessage(data.message, "error", "dailyConsultPerformance");
        setDailyConsultPerformance([]);
        setDailyConsultTotals(null);
        setPageConsult(0);
      }
    } catch (error) {
      console.log("error", error);
      toastMessage(
        "Failed to load daily consult performance data",
        "error",
        "dailyConsultPerformance"
      );
      setDailyConsultPerformance([]);
      setDailyConsultTotals(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupFilterChange = (groupKey) => {
    setSelectedGroup(groupKey);
    setPageConsult(0);
  };

  const handleSubGroupChange = (key) => {
    setSelectedSubGroup(key);
    setPageConsult(0);
    setColumnVisibilityConsult(
      SUB_GROUP_OPTIONS.includes(key)
        ? buildQuerySubGroupColumnVisibility()
        : buildConsultColumnVisibilityDefaults()
    );
  };

  const handleChangePageConsult = (_event, newPage) => {
    setPageConsult(newPage);
  };

  const handleChangeRowsPerPageConsult = (event) => {
    setRowsPerPageConsult(parseInt(event.target.value, 10));
    setPageConsult(0);
  };

  const handleColumnToggleConsult = (columnKey) => {
    setColumnVisibilityConsult((prev) => ({
      ...prev,
      [columnKey]: prev[columnKey] === false,
    }));
  };

  const handleSelectAllColumnsConsult = () => {
    const allVisible = consultColumnDefinitions.every(
      (col) => columnVisibilityConsult[col.key] !== false
    );
    const newVisibility = { ...columnVisibilityConsult };
    consultColumnDefinitions.forEach((col) => {
      newVisibility[col.key] = !allVisible;
    });
    setColumnVisibilityConsult(newVisibility);
  };

  const moveQueryGroup = (index, direction) => {
    setQueryGroupOrder((prev) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(nextIndex, 0, item);
      return next;
    });
  };

  const fetchDailyQueryProps = async () => {
    try {
      const { data } = await getDailyQueryPropsApi();
      if (data.code === 200 && data.result) {
        const order = Array.isArray(data.result.groupOrderToSendQuery)
          ? data.result.groupOrderToSendQuery
          : ["AQ", "BQ", "CQ"];
        setQueryGroupOrder(order);
        setMaximumPerDayQuery(data.result.maximumPerDayQuery ?? 3);
      }
    } catch (error) {
      console.log("error loading daily query props", error);
    }
  };

  const saveDailyQueryProps = async () => {
    const maximum = Number(maximumPerDayQuery);
    if (!Number.isInteger(maximum) || maximum < 1) {
      toastMessage("Maximum per day must be a positive whole number", "error", "dailyQueryProps");
      return;
    }
    try {
      setQueryPropsSaving(true);
      const { data } = await updateDailyQueryPropsApi({
        groupOrderToSendQuery: queryGroupOrder,
        maximumPerDayQuery: maximum,
      });
      if (data.code === 200) {
        setQueryGroupOrder(data.result?.groupOrderToSendQuery || queryGroupOrder);
        setMaximumPerDayQuery(data.result?.maximumPerDayQuery ?? maximum);
        toastMessage("Query settings saved", "success", "dailyQueryProps");
      } else {
        toastMessage(data.message || "Failed to save query settings", "error", "dailyQueryProps");
      }
    } catch (error) {
      console.log("error saving daily query props", error);
      toastMessage("Failed to save query settings", "error", "dailyQueryProps");
    } finally {
      setQueryPropsSaving(false);
    }
  };

  useEffect(() => {
    scrollToUp();
    fetchDailyConsultPerformance();
    fetchDailyQueryProps();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isColumnSelectorOpenConsult &&
        columnSelectorRefConsult.current &&
        !columnSelectorRefConsult.current.contains(event.target)
      ) {
        setIsColumnSelectorOpenConsult(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isColumnSelectorOpenConsult]);

  return (
    <>
      <style>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .column-selector-dropdown {
          animation: fadeInDown 0.2s ease-out;
        }
        .column-selector-dropdown::-webkit-scrollbar {
          width: 6px;
        }
        .column-selector-dropdown::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .column-selector-dropdown::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .column-selector-dropdown::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .daily-breakdown-table.consult-freeze-table {
          border-collapse: separate;
          border-spacing: 0;
        }
        .consult-freeze-table th:nth-child(-n + 2),
        .consult-freeze-table td:nth-child(-n + 2) {
          position: sticky;
          z-index: 2;
          background: #fff;
        }
        .consult-freeze-table th:nth-child(-n + 2) { background: #ff8989; z-index: 3; }
        .consult-freeze-table tfoot td:nth-child(-n + 2) { background: #f8f9fa; }
        .consult-freeze-table tbody tr:hover td:nth-child(-n + 2) { background: #fff9f9; }
        .consult-freeze-table th:first-child,
        .consult-freeze-table td:first-child {
          left: 0;
          width: 180px;
          min-width: 180px;
          max-width: 180px;
        }
        .consult-freeze-table th:nth-child(2),
        .consult-freeze-table td:nth-child(2) {
          left: 180px;
          min-width: 120px;
          box-shadow: 4px 0 6px -2px rgba(15, 23, 42, 0.16);
        }
      `}</style>
      <div className="WrapperArea">
        <div className="WrapperBox">
          <div className="TitleBox">
            <h4 className="Title">Daily Consult Performance</h4>
          </div>

          <div className="daily-breakdown-section">
            <div className="breakdown-header">
              <h4>Daily Consult Performance</h4>
              <div
                className="breakdown-header-controls"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "16px",
                  flexWrap: "wrap",
                }}
              >
                <div
                  className="breakdown-summary"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: "8px",
                    }}
                  >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    {GROUP_FILTER_OPTIONS.map((option) => {
                      const isActive = selectedGroup === option.key;
                      return (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => handleGroupFilterChange(option.key)}
                          style={{
                            padding: "6px 12px",
                            borderRadius: "6px",
                            border: isActive
                              ? "1px solid #c2410c"
                              : "1px solid #d1d5db",
                            background: isActive ? "#fff7ed" : "white",
                            color: isActive ? "#c2410c" : "#374151",
                            fontSize: "13px",
                            fontWeight: isActive ? 600 : 500,
                            cursor: "pointer",
                            lineHeight: 1.2,
                            transition: "all 0.15s ease",
                          }}
                          title={
                            option.key === "all"
                              ? "All groups"
                              : DAILY_GROUP_LABELS[option.key] || option.label
                          }
                        >
                          {option.key === "all"
                            ? "All"
                            : option.key === "dead"
                              ? "Dead"
                              : option.key === "query"
                                ? "Query"
                                : `Group ${option.label}`}
                        </button>
                      );
                    })}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#6b7280",
                      }}
                    >
                      Sub group
                    </span>
                    {["all", ...SUB_GROUP_OPTIONS].map((key) => {
                      const isActive = selectedSubGroup === key;
                      const colors = SUB_GROUP_COLORS[key];
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => handleSubGroupChange(key)}
                          style={{
                            padding: "6px 12px",
                            borderRadius: "6px",
                            border: colors
                              ? `1px solid ${colors.bg}`
                              : isActive
                                ? "1px solid #c2410c"
                                : "1px solid #d1d5db",
                            background: isActive ? colors?.bg || "#fff7ed" : "white",
                            color: isActive
                              ? colors?.text || "#c2410c"
                              : colors?.bg || "#374151",
                            fontSize: "13px",
                            fontWeight: isActive ? 600 : 500,
                            cursor: "pointer",
                          }}
                        >
                          {key === "all" ? "All" : key}
                        </button>
                      );
                    })}
                  </div>
                  </div>
                  <span className="summary-item">
                    <i className="fa fa-calendar"></i>
                    {dailyConsultDateRangeLabel}
                  </span>
                  <span className="summary-item">
                    <i className="fa fa-user"></i>
                    {filteredDailyConsultPerformance.length} consultants
                  </span>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="daily-consult-days-label">
                      Show last
                    </InputLabel>
                    <MuiSelect
                      labelId="daily-consult-days-label"
                      value={dailyConsultFilter}
                      label="Show last"
                      onChange={(e) => {
                        const value = e.target.value;
                        setDailyConsultFilter(value);
                        setPageConsult(0);
                        if (value !== "custom") {
                          setDailyConsultCustomStart(null);
                          setDailyConsultCustomEnd(null);
                          fetchDailyConsultPerformance(value);
                        }
                      }}
                      sx={{ height: "40px", background: "white" }}
                    >
                      {DASHBOARD_DAY_RANGE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </MuiSelect>
                  </FormControl>
                  {dailyConsultFilter === "custom" && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <DatePicker
                        selected={dailyConsultCustomStart}
                        onChange={(date) => {
                          setDailyConsultCustomStart(date);
                          if (date && dailyConsultCustomEnd) {
                            setPageConsult(0);
                            fetchDailyConsultPerformance("custom", {
                              start: date,
                              end: dailyConsultCustomEnd,
                            });
                          }
                        }}
                        maxDate={dailyConsultCustomEnd || new Date()}
                        placeholderText="Start date"
                        className="form-control"
                        dateFormat="dd-MM-yyyy"
                      />
                      <DatePicker
                        selected={dailyConsultCustomEnd}
                        onChange={(date) => {
                          setDailyConsultCustomEnd(date);
                          if (dailyConsultCustomStart && date) {
                            setPageConsult(0);
                            fetchDailyConsultPerformance("custom", {
                              start: dailyConsultCustomStart,
                              end: date,
                            });
                          }
                        }}
                        minDate={dailyConsultCustomStart}
                        maxDate={new Date()}
                        placeholderText="End date"
                        className="form-control"
                        dateFormat="dd-MM-yyyy"
                      />
                    </div>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                  }}
                >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    background: "white",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    padding: "8px 10px",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <span style={{ fontSize: "11px", fontWeight: 600, color: "#6b7280" }}>
                      Query order
                    </span>
                    {queryGroupOrder.map((group, index) => (
                      <div
                        key={group}
                        style={{ display: "flex", alignItems: "center", gap: "4px" }}
                      >
                        <span style={{ minWidth: "28px", fontSize: "13px", fontWeight: 600 }}>
                          {group}
                        </span>
                        <button
                          type="button"
                          onClick={() => moveQueryGroup(index, -1)}
                          disabled={index === 0}
                          style={{
                            border: "1px solid #d1d5db",
                            background: "white",
                            borderRadius: "4px",
                            width: "22px",
                            height: "22px",
                            cursor: index === 0 ? "default" : "pointer",
                            lineHeight: 1,
                          }}
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveQueryGroup(index, 1)}
                          disabled={index === queryGroupOrder.length - 1}
                          style={{
                            border: "1px solid #d1d5db",
                            background: "white",
                            borderRadius: "4px",
                            width: "22px",
                            height: "22px",
                            cursor: index === queryGroupOrder.length - 1 ? "default" : "pointer",
                            lineHeight: 1,
                          }}
                          title="Move down"
                        >
                          ↓
                        </button>
                      </div>
                    ))}
                  </div>
                  <label style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "11px", fontWeight: 600, color: "#6b7280" }}>
                    Max / day
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={maximumPerDayQuery}
                      onChange={(e) => setMaximumPerDayQuery(e.target.value)}
                      style={{
                        width: "72px",
                        height: "32px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        padding: "0 8px",
                        fontSize: "13px",
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={saveDailyQueryProps}
                    disabled={queryPropsSaving}
                    style={{
                      alignSelf: "flex-end",
                      padding: "8px 12px",
                      background: "#c2410c",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: queryPropsSaving ? "default" : "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  >
                    {queryPropsSaving ? "Saving" : "Save"}
                  </button>
                </div>
                <div
                  className="column-selector-wrapper"
                  ref={columnSelectorRefConsult}
                  style={{ position: "relative" }}
                >
                  <button
                    type="button"
                    className="column-selector-btn"
                    onClick={() => {
                      setIsColumnSelectorOpenConsult((prev) => !prev);
                    }}
                    style={{
                      padding: "10px 18px",
                      background: "white",
                      color: "#374151",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "500",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#f9fafb";
                      e.currentTarget.style.borderColor = "#9ca3af";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "white";
                      e.currentTarget.style.borderColor = "#d1d5db";
                    }}
                  >
                    <i
                      className="fa fa-columns"
                      style={{ fontSize: "16px", color: "#6b7280" }}
                    ></i>
                    <span>Columns</span>
                    <span
                      style={{
                        background: "#f3f4f6",
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "500",
                        color: "#6b7280",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: "1",
                      }}
                    >
                      {visibleConsultColumns.length}/
                      {consultColumnDefinitions.length}
                    </span>
                  </button>
                  {isColumnSelectorOpenConsult && (
                    <div
                      className="column-selector-dropdown"
                      style={{
                        position: "absolute",
                        top: "calc(100% + 8px)",
                        right: 0,
                        background: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "12px",
                        boxShadow:
                          "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)",
                        padding: "0",
                        minWidth: "320px",
                        maxWidth: "400px",
                        zIndex: 1000,
                        maxHeight: "500px",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "16px 20px",
                          borderBottom: "1px solid #e5e7eb",
                          background: "#f9fafb",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <i
                            className="fa fa-sliders"
                            style={{ color: "#6b7280", fontSize: "16px" }}
                          ></i>
                          <strong
                            style={{
                              fontSize: "16px",
                              color: "#111827",
                              fontWeight: "600",
                            }}
                          >
                            Select Columns
                          </strong>
                        </div>
                        <button
                          type="button"
                          onClick={handleSelectAllColumnsConsult}
                          style={{
                            background: "white",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            padding: "6px 12px",
                            fontSize: "12px",
                            cursor: "pointer",
                            color: "#374151",
                            fontWeight: "500",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f3f4f6";
                            e.currentTarget.style.borderColor = "#9ca3af";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "white";
                            e.currentTarget.style.borderColor = "#d1d5db";
                          }}
                        >
                          {consultColumnDefinitions.every(
                            (col) => columnVisibilityConsult[col.key] !== false
                          )
                            ? "Deselect All"
                            : "Select All"}
                        </button>
                      </div>
                      <div
                        style={{
                          maxHeight: "420px",
                          overflowY: "auto",
                          padding: "8px",
                          scrollbarWidth: "thin",
                          scrollbarColor: "#cbd5e1 #f1f5f9",
                        }}
                      >
                        {consultColumnDefinitions.map((column) => {
                          const isChecked = columnVisibilityConsult[column.key] !== false;
                          const getColumnIcon = (type) => {
                            switch (type) {
                              case "consultant":
                                return "fa-user";
                              case "group":
                                return "fa-users";
                              case "currency":
                                return "fa-rupee-sign";
                              case "percentage":
                                return "fa-percent";
                              default:
                                return "fa-chart-bar";
                            }
                          };

                          return (
                            <label
                              key={column.key}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "12px 16px",
                                cursor: "pointer",
                                borderRadius: "6px",
                                marginBottom: "2px",
                                transition: "background 0.2s ease",
                                background: isChecked ? "#f3f4f6" : "transparent",
                                border: "1px solid transparent",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#f9fafb";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = isChecked
                                  ? "#f3f4f6"
                                  : "transparent";
                              }}
                            >
                              <div
                                style={{
                                  position: "relative",
                                  marginRight: "12px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() =>
                                    handleColumnToggleConsult(column.key)
                                  }
                                  style={{
                                    width: "18px",
                                    height: "18px",
                                    cursor: "pointer",
                                    accentColor: "#6b7280",
                                  }}
                                />
                              </div>
                              <i
                                className={`fa ${getColumnIcon(column.type)}`}
                                style={{
                                  marginRight: "12px",
                                  color: "#9ca3af",
                                  fontSize: "14px",
                                  width: "18px",
                                  textAlign: "center",
                                }}
                              ></i>
                              <span
                                style={{
                                  fontSize: "14px",
                                  color: "#374151",
                                  fontWeight: "400",
                                  flex: 1,
                                }}
                              >
                                {column.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      <div
                        style={{
                          padding: "12px 20px",
                          borderTop: "1px solid #e5e7eb",
                          background: "#f9fafb",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            fontWeight: "400",
                          }}
                        >
                          {visibleConsultColumns.length} of{" "}
                          {consultColumnDefinitions.length} selected
                        </span>
                        <button
                          type="button"
                          onClick={() => setIsColumnSelectorOpenConsult(false)}
                          style={{
                            background: "white",
                            color: "#374151",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            padding: "6px 16px",
                            fontSize: "12px",
                            cursor: "pointer",
                            fontWeight: "500",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f3f4f6";
                            e.currentTarget.style.borderColor = "#9ca3af";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "white";
                            e.currentTarget.style.borderColor = "#d1d5db";
                          }}
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                </div>
              </div>
            </div>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "400px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <div className="table-container">
                  <table className="daily-breakdown-table consult-freeze-table">
                    <thead>
                      <tr>
                        {visibleConsultColumns.map((column) => (
                          <th
                            key={column.key}
                            style={metricGroupBorder(column)}
                          >
                            <Tooltip
                              arrow
                              placement="top"
                              enterDelay={250}
                              title={
                                <Box sx={{ maxWidth: 380, py: 0.5 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontSize: 13 }}
                                  >
                                    {column.description}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{
                                      mt: 1,
                                      display: "block",
                                      opacity: 0.92,
                                    }}
                                  >
                                    <strong>Example:</strong> {column.example}
                                  </Typography>
                                </Box>
                              }
                            >
                              <span
                                style={{
                                  cursor: "help",
                                  borderBottom: "1px dotted #94a3b8",
                                }}
                              >
                                {column.label}
                              </span>
                            </Tooltip>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDailyConsultPerformance.length > 0 ? (
                        paginatedDailyConsultPerformance.map((row, index) => {
                          const rowColors =
                            selectedGroup === "query" ? queryRowColors(row) : null;
                          return (
                          <tr
                            key={`consult-${row.consultantId ?? row.consultantName ?? index}-${index}`}
                            style={rowColors || undefined}
                          >
                            {visibleConsultColumns.map((column) => {
                              const cellValue = getConsultCellValue(
                                row,
                                column
                              );
                              const cellClassName =
                                column.type === "consultant" ||
                                column.type === "group"
                                  ? "date-cell"
                                  : column.type === "currency"
                                    ? "currency-cell"
                                    : `number-cell ${column.className || ""}`;
                              return (
                                <td
                                  key={column.key}
                                  className={cellClassName}
                                  style={{
                                    ...metricGroupBorder(column),
                                    ...(rowColors || {}),
                                  }}
                                >
                                  {column.type === "consultant" ||
                                  column.type === "group" ? (
                                    <div className="date-info">
                                      <span
                                        className="date-text"
                                        style={rowColors ? { color: rowColors.color } : undefined}
                                      >
                                        {cellValue}
                                      </span>
                                    </div>
                                  ) : column.type === "percentage" ? (
                                    `${cellValue}%`
                                  ) : (
                                    cellValue
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan={visibleConsultColumns.length}
                            className="no-data"
                          >
                            <div className="no-data-content">
                              <i className="fa fa-chart-bar"></i>
                              <p>
                                {selectedGroup !== "all" ||
                                selectedSubGroup !== "all"
                                  ? "No consultants match the selected filters"
                                  : "No data available for the selected period"}
                              </p>
                              <small>
                                {selectedGroup !== "all" ||
                                selectedSubGroup !== "all"
                                  ? "Try another group or sub group"
                                  : "Try selecting a different time range"}
                              </small>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                    {filteredDailyConsultPerformance.length > 0 && (
                      <tfoot>
                        <tr className="totals-row">
                          {visibleConsultColumns.map((column) => {
                            const totalsValue = getConsultTotalsValue(
                              column,
                              selectedGroup === "all" &&
                                selectedSubGroup === "all" &&
                                dailyConsultTotals
                                ? dailyConsultTotals
                                : filteredDailyConsultPerformance
                            );
                            const cellClassName =
                              column.type === "consultant" ||
                              column.type === "group"
                                ? "totals-label"
                                : column.type === "currency"
                                  ? "totals-currency"
                                  : `totals-value ${column.className || ""}`;
                            return (
                              <td
                                key={column.key}
                                className={cellClassName}
                                style={metricGroupBorder(column)}
                              >
                                {column.type === "percentage"
                                  ? `${totalsValue}%`
                                  : totalsValue}
                              </td>
                            );
                          })}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                {filteredDailyConsultPerformance.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 2,
                      px: 2,
                      pb: 2,
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Showing {pageConsult * rowsPerPageConsult + 1} to{" "}
                      {Math.min(
                        (pageConsult + 1) * rowsPerPageConsult,
                        filteredDailyConsultPerformance.length
                      )}{" "}
                      of {filteredDailyConsultPerformance.length} entries
                    </Typography>
                    <TablePagination
                      component="div"
                      count={filteredDailyConsultPerformance.length}
                      page={pageConsult}
                      onPageChange={handleChangePageConsult}
                      rowsPerPage={rowsPerPageConsult}
                      onRowsPerPageChange={handleChangeRowsPerPageConsult}
                      rowsPerPageOptions={[5, 10, 20, 30, 50, 100]}
                      labelRowsPerPage="Rows per page:"
                    />
                  </Box>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DailyConsultPerformance;
